import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import ApiServices from "../../Services/ApiServices/ApiServices";

const initialForm = {
  customer_name: "",
  customer_phone: "",
  address: "",
  zip_code: "",
  service_needed: "",
  notes: "",
};

const AddRefer = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const userData = JSON.parse(sessionStorage.getItem("user"));
  const referralSourceId = userData?.id;

  const phoneRegex = /^[0-9]{10}$/;
  const zipRegex = /^[0-9]{5}$/;

  /* ---------------- CHECK REFERRAL ACCESS ---------------- */
  const checkReferralAccess = async () => {
    if (!referralSourceId) {
      setHasAccess(false);
      setCheckingAccess(false);
      return;
    }

    try {
      setCheckingAccess(true);
      const res = await ApiServices.get(
        `/referral-sources/${referralSourceId}/`
      );

      const allow = res?.data?.refer_allow && res?.data?.is_active;
      setHasAccess(!!allow);
    } catch {
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  useEffect(() => {
    checkReferralAccess();
  }, [referralSourceId]);

  /* ---------------- HANDLE CHANGE ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "customer_phone") {
      if (!/^\d*$/.test(value) || value.length > 10) return;
    }

    if (name === "zip_code") {
      if (!/^\d*$/.test(value) || value.length > 5) return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async () => {
    if (!hasAccess) return;

    if (!form.customer_name || !form.customer_phone || !form.zip_code) {
      setSnack({
        open: true,
        message: "Customer Name, Phone, and ZIP Code are required",
        severity: "error",
      });
      return;
    }

    if (!phoneRegex.test(form.customer_phone)) {
      setSnack({
        open: true,
        message: "Phone number must be exactly 10 digits",
        severity: "error",
      });
      return;
    }

    if (!zipRegex.test(form.zip_code)) {
      setSnack({
        open: true,
        message: "ZIP code must be exactly 5 digits",
        severity: "error",
      });
      return;
    }

    const payload = {
      customer_name: form.customer_name,
      customer_phone: form.customer_phone,
      address: form.address,
      zip_code: form.zip_code,
      service_needed: form.service_needed,
      referral_source: referralSourceId,
      notes: form.notes,
    };

    try {
      setLoading(true);
      await ApiServices.post("/referral-leads/", payload);

      setSnack({
        open: true,
        message: "Referral lead submitted successfully!",
        severity: "success",
      });

      setForm(initialForm);
    } catch (error) {
      setSnack({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to submit referral. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- LOADING ACCESS CHECK ---------------- */
  if (checkingAccess) {
    return (
      <Box textAlign="center" py={6}>
        <CircularProgress />
        <Typography mt={2}>Checking referral access...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 850, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Card sx={{ borderRadius: 3, boxShadow: 6 }}>
        <CardContent>
          {/* ACCESS DENIED MESSAGE */}
          {!hasAccess && (
            <Box
              sx={{
                mb: 3,
                p: 3,
                borderRadius: 2,
                backgroundColor: "#fff4f4",
                borderLeft: "6px solid #d32f2f",
              }}
            >
              <Typography variant="h6" fontWeight={600} color="error">
                Referral Access Disabled
              </Typography>
              <Typography variant="body2" mt={1}>
                You no longer have access to submit referrals. Please contact the
                Rite Home Pros customer support team for assistance.
              </Typography>
            </Box>
          )}

          {/* INFO MESSAGE */}
          {hasAccess && (
            <Box
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: 2,
                backgroundColor: "#f4f9f4",
                borderLeft: "6px solid #2e7d32",
              }}
            >
              <Typography variant="h6" fontWeight={600} color="primary">
                Referral Lead Submission
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Submit client details who may require Rite Home Pros services.
                Our team will connect with them and take care of the rest.
              </Typography>
            </Box>
          )}

          {/* FORM */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              label="Customer Name *"
              name="customer_name"
              value={form.customer_name}
              onChange={handleChange}
              fullWidth
              disabled={!hasAccess}
            />

            <TextField
              label="Customer Phone *"
              name="customer_phone"
              value={form.customer_phone}
              onChange={handleChange}
              fullWidth
              inputProps={{ inputMode: "numeric" }}
              disabled={!hasAccess}
            />

            <TextField
              label="ZIP Code *"
              name="zip_code"
              value={form.zip_code}
              onChange={handleChange}
              fullWidth
              inputProps={{ inputMode: "numeric" }}
              disabled={!hasAccess}
            />

            <TextField
              label="Address"
              name="address"
              value={form.address}
              onChange={handleChange}
              fullWidth
              disabled={!hasAccess}
            />

            <TextField
              label="Service Needed"
              name="service_needed"
              value={form.service_needed}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={3}
              disabled={!hasAccess}
            />

            <TextField
              label="Additional Notes"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={4}
              disabled={!hasAccess}
            />

            <Button
              variant="contained"
              size="large"
              disabled={!hasAccess || loading}
              onClick={handleSubmit}
              sx={{ py: 1.6, fontWeight: 600, borderRadius: 2 }}
            >
              {loading ? "Submitting..." : "Submit Referral"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddRefer;
